import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { QueryParamsModel } from '../../../../../../core/_base/crud';
import { fromEvent, merge } from 'rxjs';
import { AppState } from '../../../../../../core/reducers';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { FollowupReasonEditComponent } from '../followup-reason-edit/followup-reason-edit.component';
import { FollowupReasonsDataSource } from '../../../../../../core/lists/_data-sources/followup-reasons.datasource';
import { FollowupReasonsPageRequested } from '../../../../../../core/lists/_actions/followup-reason.actions';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationService } from '../../../../../../core/services/navigation.service';

@Component({
  selector: 'kt-followup-reasons-list',
  templateUrl: './followup-reasons-list.component.html',
  styleUrls: ['./followup-reasons-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FollowupReasonsListComponent implements OnInit, OnDestroy {
  dataSource: FollowupReasonsDataSource;
  displayedColumns = ['name', 'actions'];
  searchBarColumns = ['searchReason', 'searchActions'];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchReasonInput = '';
  searchIsActiveSelect = 'true';
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  searchForm: FormGroup;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize: any;

  constructor(
    private navigationService: NavigationService,
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private store: Store<AppState>,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'id';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadReasonsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadReasonsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    this.dataSource = new FollowupReasonsDataSource(this.store);

    this.searchForm = this.fb.group({
      search: [],
      status: ['true'],
    });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;
    }

    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadReasonsList();
    });
    this.subscriptions.push({ id: 5, sub: routeSubscription });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadReasonsList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(new FollowupReasonsPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?: any): any {
    const filter: any = {};
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchReasonInput = appliedFilters.reason;
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'desc';
      this.sort.active = this.filtersApplied?.sortActive || 'id';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.reason = this.searchReasonInput;
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'desc';
    filter.sortActive = this.sort.active || 'id';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  editReason(reason) {
    const dialogRef = this.dialog.open(FollowupReasonEditComponent, {
      data: { mode: 'UPDATE', reason },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.loadReasonsList();
    });
  }

  createReason() {
    const dialogRef = this.dialog.open(FollowupReasonEditComponent, {
      data: { mode: 'CREATE' },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.loadReasonsList();
    });
  }

  reloadReasonsList(): void {
    this.paginator.pageIndex = 0;
    this.loadReasonsList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();
    this.searchReasonInput = '';
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadReasonsList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadReasonsList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();
    this.reloadReasonsList();
  }
}
